import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ReactComponent as InfoSvg } from "../../../assets/images/icons/info.svg";
import LoadingAnimation from "../../../components/animation/LoadingAnimation";
import AdminActionsButton from "../../../components/button/AdminActionsButton";
import InputfieldWithDropDown from "../../../components/input/InputfieldWithDropDown";
import SearchInputBar from "../../../components/input/SearchInputBar";
import AdminSidebar from "../../../components/sidebar/AdminSidebar";
import H3 from "../../../components/typography/H3";
import { productService } from "../../../service/ProductService";
import colors from "../../../theme/color/ColorTheme";
import fontSize from "../../../theme/fonts/FontTheme";
import ProductTable from "./components/ProductTable";
const Wrapper = styled.div`
  display: flex;
  width: 100%;
  min-height: 100vh;

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const Nav = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  z-index: 10;

  @media (max-width: 1024px) {
    position: relative;
    height: auto;
    width: 100%;
  }
`;

const Content = styled.div`
  flex: 1;
  background-color: ${colors.backgroundTintPrimary};
  padding: 20px;
  margin-left: 260px;
  width: calc(100% - 260px);
  @media (max-width: 1200px) {
    margin-left: 80px;
    width: calc(100% - 80px);
    padding: 15px;
  }

  @media (max-width: 1024px) {
    margin-left: 0;
    width: 100%;
    padding: 10px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 24px;
  border-radius: 12px;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  @media (max-width: 768px) {
    padding: 20px 15px;
    border-radius: 8px;
  }
`;
const SortFilterContainer = styled.div`
  padding: 24px;
  margin-top: 20px;
  margin-bottom: 30px;
  background-color: ${colors.backgroundTintSecondary};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
`;

const InfoTextSpan = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${fontSize.xs};
`;
const FixedInputWrapper = styled.div`
  width: 20%;
  & > div {
    width: 100%;
  }
  input,
  select {
    width: 100%;
    box-sizing: border-box;
  }
`;
const FilterButton = styled.button`
  padding: 8px 16px;
  margin: 5px 0px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  border: ${(props) =>
    props.active ? `1.5px solid ${colors.dash}` : "1px solid transparent"};
  background: ${(props) =>
    props.active ? colors.backgroundPrimary : colors.backgroundTintSecondary};
  color: ${(props) => (props.active ? colors.textPrimary : "#818181")};
  transition: all 0.2s ease;
  &:hover {
    background: ${(props) =>
      props.active ? colors.backgroundPrimary : colors.backgroundTintPrimary};
  }
`;
function ProductsPage() {
  const [selectedCategory, setSelectedCategory] = useState("");
  const [loading, setLoading] = useState();
  const [products, setProducts] = useState();
  const [reload, setReload] = useState();
  const [page, setPage] = useState(1);
  const [pageType, setPageType] = useState("default");
  const [searchTerm, setSearchTerm] = useState();
  const navigate = useNavigate();
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const handleSearch = () => {
    if (searchTerm || selectedCategory !== "") {
      setPageType("search");
      setReload(true);
      setPage(1);
    }
    return;
  };

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setLoading(true);

    const fetchLatestProducts = async () => {
      setReload(false);
      productService.getLatestProducts(
        page,
        (products) => {
          setProducts(products);
          setLoading(false);
        },
        {
          default: (message) => {
            alert(message);
            setProducts([]);
            setLoading(false);
          },
        }
      );
    };
    const fetchSearchProducts = async () => {
      setReload(false);
      const query = {
        query: searchTerm,
        ...(selectedCategory !== "" && { categories: [selectedCategory] }),
      };
      productService.searchProducts(
        query,
        page,
        (products) => {
          setProducts(products.products);
          setLoading(false);
        },
        {
          default: (message) => {
            alert(message);
            setProducts([]);
            setLoading(false);
          },
        }
      );
    };
    if (pageType === "default") {
      fetchLatestProducts();
    } else {
      fetchSearchProducts();
    }
  }, [reload, page, pageType]);

  return (
      <Wrapper>
        <Nav>
          <AdminSidebar collapsed={window.innerWidth < 1200} />
        </Nav>
        <Content>
          <Container>
            <H3>Products</H3>

            <SortFilterContainer>
              <InfoTextSpan>
                <InfoSvg />
                <p>
                  Tip search by Product ID: Each product is provided with a
                  unique ID, which you can rely on to find the exact product you
                  need.
                </p>
              </InfoTextSpan>
              <ControlsRow>
                <AdminActionsButton
                  children={"+ Add Product"}
                  onClick={() => {
                    navigate("/admin/add-product");
                  }}
                />
                <SearchInputBar
                  onChange={(val) => {
                    setSearchTerm(val);
                  }}
                  value={searchTerm}
                />
                <FixedInputWrapper>
                  <InputfieldWithDropDown
                    type="dropdown"
                    value={selectedCategory}
                    onChange={(val) => setSelectedCategory(val)}
                    options={[
                      "Clothing",
                      "Shoes",
                      "Accessories",
                      "Electronics",
                      "Bags",
                    ]}
                    placeholder="Category"
                  />
                </FixedInputWrapper>
                <AdminActionsButton
                  children={"Search"}
                  onClick={() => {
                    handleSearch();
                  }}
                />
              </ControlsRow>
            </SortFilterContainer>
            {pageType === "search" && (
              <FilterButton
                onClick={() => {
                  setPageType("default");
                  setSearchTerm("");
                  setSelectedCategory("");
                }}
              >
                Cancel Search
              </FilterButton>
            )}
            {loading && <LoadingAnimation height={"500px"} />}
            {products && !loading && (
              <ProductTable
                products={products}
                page={page}
                updatePage={setPage}
              />
            )}
          </Container>
        </Content>
      </Wrapper>
    
  );
}

export default ProductsPage;
